import { ENVIRONMENTS_NAMES, EVENT_TYPES, EventTypes, SORTABLE_EVENT_COLUMNS } from '../../types';
import { validationSchema } from '../../types/validation';
import * as z from 'zod/v4';
import {
  cardDisplaySectionSchema,
  chainOfferSectionSchema,
  eventBoxSectionSchema,
  eventCurrencySectionSchema,
  eventOverrideSectionSchema,
  eventQuestSectionSchema,
  exchangeShopSectionSchema,
  growthFundSectionSchema,
  notificationSectionSchema,
  piggyBankSectionSchema,
  purchaseSectionSchema,
  rewardSectionSchema,
  scheduleSectionSchema,
  stepUpOfferSectionSchema,
  userTargetingSectionSchema,
  wallOfferSectionSchema,
} from './sections/event-sections.schemas';
import { eventLeaderboardSectionSchema } from './sections/event-leaderboard-schema';
import { gatchaSectionSchema } from './sections/gatcha-event-schema';
import { carnivalSectionSchema } from './sections/carnival-event-schema';
import { boxedGachaSectionSchema } from './sections/boxed-gacha-event-schema';
import { heroCardDropRatesSectionSchema } from './sections/hero-card-drop-rates-schema';
import { combatModifiersSectionSchema } from './sections/combat-modifiers-schema';
import { increasedRewardsSectionSchema } from './sections/increased-rewards-schema';
import { modesOpenSectionSchema } from './sections/modes-open-schema';
import { costReductionsSectionSchema } from './sections/cost-reductions-schema';
import { additionalModeChancesSectionSchema } from './sections/additional-mode-chances-schema';

import { EnumParams, EventMap } from './events.types';
import { userSchema } from '../users/users.schemas';
import { featuredContentSectionSchema } from './sections/generic-interstitial-schema';

type TypedComponents = Record<string, z.ZodSchema>;

type BaseParams = {
  enums?: EnumParams;
};

type GetEventComponentsSchemaParams<T extends TypedComponents> = BaseParams & {
  typedComponents: T;
};

export const getEventComponentsSchema = <T extends TypedComponents>({
  enums,
  typedComponents,
}: GetEventComponentsSchemaParams<T>) =>
  z.object({
    schedule: scheduleSectionSchema.optional(),
    cardDisplay: cardDisplaySectionSchema(enums).optional(),
    userTargeting: userTargetingSectionSchema(enums).optional(),
    notification: notificationSectionSchema.optional(),
    ...typedComponents,
  });

export const basicOfferComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      purchase: purchaseSectionSchema(enums),
      reward: rewardSectionSchema(enums),
    },
  });

export const eventBoxComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      eventBox: eventBoxSectionSchema(enums),
    },
  });

export const chainOfferComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      chainOffer: chainOfferSectionSchema(enums),
    },
  });

export const stepUpOfferComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      stepUpOffer: stepUpOfferSectionSchema(enums),
    },
  });

export const growthFundComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      growthFund: growthFundSectionSchema(enums),
    },
  });

export const piggyBankComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      piggyBank: piggyBankSectionSchema(enums),
    },
  });

export const wallOfferComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      wallOffer: wallOfferSectionSchema(enums),
    },
  });

export const exchangeShopComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      exchangeShop: exchangeShopSectionSchema(enums),
    },
  });

export const eventQuestComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      eventQuest: eventQuestSectionSchema(enums),
    },
  });

export const eventCurrencyComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      eventCurrency: eventCurrencySectionSchema(enums),
    },
  });

export const gatchaComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      chest: gatchaSectionSchema(enums),
    },
  });

export const carnivalComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      carnival: carnivalSectionSchema(enums),
    },
  });

export const boxedGachaComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      boxedGacha: boxedGachaSectionSchema(enums),
    },
  });

export const eventOverrideComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      eventOverride: eventOverrideSectionSchema(enums),
    },
  });

export const eventLeaderboardComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      eventLeaderboard: eventLeaderboardSectionSchema(enums),
    },
  });

export const genericInterstitialComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      featuredContent: featuredContentSectionSchema(enums).optional(),
    },
  });

export const heroCardDropRatesComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      heroCardDropRates: heroCardDropRatesSectionSchema(enums),
    },
  });

export const combatModifiersComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      combatModifiers: combatModifiersSectionSchema(enums),
    },
  });

export const increaseRewardsComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      increasedRewards: increasedRewardsSectionSchema(enums),
    },
  });

export const modesOpenComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      modesOpen: modesOpenSectionSchema(enums),
    },
  });

export const costReductionsComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      costReductions: costReductionsSectionSchema(enums),
    },
  });

export const additionalModeChancesComponents = ({ enums }: BaseParams = {}) =>
  getEventComponentsSchema({
    enums,
    typedComponents: {
      additionalModeChances: additionalModeChancesSectionSchema(enums),
    },
  });

const levelRangeSchema = z.object({
  from: z.coerce.number().optional(),
  to: z.coerce.number().optional().nullable(),
});

export const deadLetterQueueSchema = z
  .object({
    status: z.enum(['PENDING', 'SENT', 'FAILED']).optional().nullable(),
    errorResponse: z.array(z.string()).optional().nullable(),
  })
  .optional()
  .nullable();

export const getEventComponentsByType = <T extends EventTypes>(
  eventType: T,
  { enums }: BaseParams,
): EventMap[T] => {
  switch (eventType) {
    case 'BasicOffer':
      return basicOfferComponents({ enums }) as EventMap[T];
    case 'Carnival':
      return carnivalComponents({ enums }) as EventMap[T];
    case 'EventBox':
      return eventBoxComponents({ enums }) as EventMap[T];
    case 'ChainOffer':
      return chainOfferComponents({ enums }) as EventMap[T];
    case 'Gatcha':
      return gatchaComponents({ enums }) as EventMap[T];
    case 'BoxedGacha':
      return boxedGachaComponents({ enums }) as EventMap[T];
    case 'GrowthFund':
      return growthFundComponents({ enums }) as EventMap[T];
    case 'PiggyBank':
      return piggyBankComponents({ enums }) as EventMap[T];
    case 'StepUpOffer':
      return stepUpOfferComponents({ enums }) as EventMap[T];
    case 'WallOffer':
      return wallOfferComponents({ enums }) as EventMap[T];
    case 'ExchangeShop':
      return exchangeShopComponents({ enums }) as EventMap[T];
    case 'EventQuest':
      return eventQuestComponents({ enums }) as EventMap[T];
    case 'EventCurrency':
      return eventCurrencyComponents({ enums }) as EventMap[T];
    case 'EventOverride':
      return eventOverrideComponents({ enums }) as EventMap[T];
    case 'EventLeaderboard':
      return eventLeaderboardComponents({ enums }) as EventMap[T];
    case 'GenericInterstitial':
      return genericInterstitialComponents({ enums }) as EventMap[T];
    case 'HeroCardDropRates':
      return heroCardDropRatesComponents({ enums }) as EventMap[T];
    case 'CombatModifiers':
      return combatModifiersComponents({ enums }) as EventMap[T];
    case 'IncreaseRewards':
      return increaseRewardsComponents({ enums }) as EventMap[T];
    case 'ModesOpen':
      return modesOpenComponents({ enums }) as EventMap[T];
    case 'CostReductions':
      return costReductionsComponents({ enums }) as EventMap[T];
    case 'AdditionalModeChances':
      return additionalModeChancesComponents({ enums }) as EventMap[T];
    default:
      throw new Error(`Event type ${eventType} not supported`);
  }
};

export const parentInheritFieldsSchema = z.object({
  inheritParentDisplay: z.boolean().default(false).optional(),
  inheritParentSchedule: z.boolean().default(false).optional(),
  inheritParentUserTargeting: z.boolean().default(false).optional(),
  inheritParentNotification: z.boolean().default(false).optional(),
});

export const getEventComponentsAndType = ({ enums }: Pick<BaseParams, 'enums'> = {}) =>
  z
    .discriminatedUnion('eventType', [
      z.object({
        eventType: z.literal('BasicOffer'),
        components: basicOfferComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('Carnival'),
        components: carnivalComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('EventBox'),
        components: eventBoxComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('EventOverride'),
        components: eventOverrideComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('ChainOffer'),
        components: chainOfferComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('StepUpOffer'),
        components: stepUpOfferComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('GrowthFund'),
        components: growthFundComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('PiggyBank'),
        components: piggyBankComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('Gatcha'),
        components: gatchaComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('BoxedGacha'),
        components: boxedGachaComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('WallOffer'),
        components: wallOfferComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('ExchangeShop'),
        components: exchangeShopComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('EventQuest'),
        components: eventQuestComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('EventCurrency'),
        components: eventCurrencyComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('EventLeaderboard'),
        components: eventLeaderboardComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('GenericInterstitial'),
        components: genericInterstitialComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('HeroCardDropRates'),
        components: heroCardDropRatesComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('CombatModifiers'),
        components: combatModifiersComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('IncreaseRewards'),
        components: increaseRewardsComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('ModesOpen'),
        components: modesOpenComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('CostReductions'),
        components: costReductionsComponents({ enums }),
      }),
      z.object({
        eventType: z.literal('AdditionalModeChances'),
        components: additionalModeChancesComponents({ enums }),
      }),
    ])
    .and(
      z.object({
        parentId: z.uuid().optional().nullable(),
        originalId: z.uuid().optional().nullable(),
        ...parentInheritFieldsSchema.shape,
      }),
    )
    .refine(({ components, parentId, inheritParentSchedule, inheritParentDisplay }) => {
      if (!parentId) {
        return !!components.schedule && !!components.cardDisplay;
      }
      if (!inheritParentSchedule && !components.schedule) {
        return false;
      }
      if (!inheritParentDisplay && !components.cardDisplay) {
        return false;
      }

      return true;
    });

export const eventComponentNoEnum = getEventComponentsAndType();

z.globalRegistry.add(eventComponentNoEnum, { id: 'eventComponentsSchema' });

export const eventBaseSchema = z.object({
  id: z.uuid(),
  canonicalId: z.uuid(),
  name: z.string(),
  enabled: z.boolean(),
  QAApproved: z.boolean(),
  formatVersion: z.number().default(1),
  start: z.coerce.date().optional().nullable(),
  end: z.coerce.date().optional().nullable(),
  createdAt: z.coerce.date(),
  lastUpdatedAt: z.coerce.date(),
  lastModifiedBy: userSchema.pick({ name: true, email: true }),
  cohortDayFrom: z.number().optional().nullable(),
  cohortDayTo: z.number().optional().nullable(),
  collectionId: z.uuid().optional().nullable(),
  originalId: z.uuid().optional().nullable(),
  levelRanges: z.array(levelRangeSchema).optional(),
  ageRanges: z
    .array(z.object({ from: z.number().optional(), to: z.number().optional() }))
    .optional(),
  environment: z.enum(ENVIRONMENTS_NAMES),
  deadLetterQueue: deadLetterQueueSchema,
  hasBrokenRefs: z.boolean().optional(),
});

export const eventSchema = eventBaseSchema.and(eventComponentNoEnum);

z.globalRegistry.add(eventSchema, { id: 'eventFullSchema' });

export const getEventsQuery = z.object({
  range: z
    .object({
      from: z.coerce.date().optional(),
      to: z.coerce.date().optional(),
    })
    .optional(),
  env: z.enum(ENVIRONMENTS_NAMES),
  getGrandChildren: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  collectionIds: z.uuid().array().optional(),
  search: z.string().optional(),
  page: z.coerce.number().optional(),
  pageSize: z.coerce.number().optional(),
  sortBy: z.enum(SORTABLE_EVENT_COLUMNS).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  inExperiment: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  noChildRelation: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  parentChildRelation: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  qa_approved: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  enabled: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  event_type: z.array(z.enum(EVENT_TYPES)).optional(),
  permanent: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  checkBrokenRefs: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  levelRange: z
    .object({
      from: z.coerce.number().optional(),
      to: z.coerce.number().optional(),
    })
    .optional(),
  serverRange: z
    .object({
      from: z.coerce.number().optional(),
      to: z.coerce.number().optional(),
    })
    .optional(),
  withoutServerTargeting: z
    .preprocess(val => val === 'true' || val === '1', z.boolean())
    .optional(),
});

export const createBaseEventSchemaBody = eventBaseSchema
  .pick({
    name: true,
    start: true,
    end: true,
    QAApproved: true,
    enabled: true,
    cohortDayFrom: true,
    cohortDayTo: true,
    collectionId: true,
    originalId: true,
    levelRanges: true,
  })
  .extend({ canonicalId: z.uuid().optional() });

export const createEventSchemaBody = createBaseEventSchemaBody.and(eventComponentNoEnum);

export const updateEventSchemaBody = createBaseEventSchemaBody
  .partial()
  .extend({
    id: z.uuid(),
  })
  .and(eventComponentNoEnum);

export const createEventSchema = validationSchema({
  body: createEventSchemaBody,
  params: z.object({
    env: z.enum(ENVIRONMENTS_NAMES),
  }),
  response: {
    201: z.object({
      event: eventBaseSchema
        .omit({ lastModifiedBy: true, environment: true })
        .and(eventComponentNoEnum),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const updateEventSchema = validationSchema({
  body: updateEventSchemaBody,
  params: z.object({
    env: z.enum(ENVIRONMENTS_NAMES),
    id: z.uuid(),
  }),
  response: {
    200: z.object({
      event: eventBaseSchema
        .omit({ lastModifiedBy: true, environment: true })
        .and(eventComponentNoEnum),
    }),
    404: z.object({
      errorMessage: z.string(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const deleteEventSchema = validationSchema({
  response: {
    404: z.object({
      errorMessage: z.string(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
    204: z.object({}),
  },
  params: z.object({
    id: z.uuid(),
  }),
});

export const eventMiniSchema = eventBaseSchema.and(
  z.object({
    ...parentInheritFieldsSchema.shape,
    eventType: z.enum(EVENT_TYPES),
  }),
);

export const getEventsSummarySchema = validationSchema({
  response: {
    200: z.object({
      events: z.array(
        z.object({
          event: eventMiniSchema,
          parentEvent: eventMiniSchema.nullable().optional(),
        }),
      ),
      total: z.number(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
  querystring: getEventsQuery,
});

export const getEventsSchema = validationSchema({
  response: {
    200: z.object({
      events: z.array(
        z.object({
          event: eventSchema,
          parentEvent: eventSchema
            .and(z.object({ parent: eventSchema.nullable().optional() }))
            .nullable()
            .optional(),
        }),
      ),
      total: z.number(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
  querystring: getEventsQuery,
});

export const getEventSchema = validationSchema({
  response: {
    200: z.object({
      event: eventSchema,
      parentEvent: eventSchema
        .and(z.object({ parent: eventSchema.nullable().optional() }))
        .nullable()
        .optional(),
    }),
    404: z.object({
      errorMessage: z.string(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
  params: z.object({
    id: z.uuid(),
  }),
});

export const getEventByCanonicalIdSchema = validationSchema({
  response: {
    200: z.object({
      event: eventSchema,
      parentEvent: eventSchema
        .and(z.object({ parent: eventSchema.nullable().optional() }))
        .nullable()
        .optional(),
    }),
    404: z.object({
      errorMessage: z.string(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
  params: z.object({
    canonicalId: z.uuid(),
    env: z.enum(ENVIRONMENTS_NAMES),
  }),
});

export const getChildEventsSchema = validationSchema({
  response: {
    200: z.object({
      children: z.array(
        z.object({
          event: eventSchema,
          children: eventSchema.array(),
          parentEvent: eventSchema.nullable().optional(),
        }),
      ),
    }),
    404: z.object({
      errorMessage: z.string(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
  params: z.object({
    id: z.uuid(),
  }),
});

export const bulkCopyEventsSchema = validationSchema({
  body: z.object({
    parentEventId: z.uuid(),
    targetEnvironment: z.enum(ENVIRONMENTS_NAMES),
  }),
  response: {
    201: z.object({
      success: z.boolean(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const duplicateEventSchema = validationSchema({
  body: z.object({
    eventId: z.uuid(),
  }),
  response: {
    201: z.object({
      success: z.boolean(),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const scheduleRecurringEventsSchema = validationSchema({
  body: z.object({
    parentEventId: z.uuid(),
    frequency: z.enum(['daily', 'weekly', 'monthly']),
    occurrences: z.number().min(1).max(52),
    markAsActive: z.boolean().optional(),
    weekdays: z.array(z.number().min(0).max(6)).optional(), // 0 = Sunday, 6 = Saturday
  }),
  response: {
    201: z.object({
      copiedCount: z.number(),
      warnings: z.array(z.string()),
    }),
    500: z.object({
      errorMessage: z.string(),
    }),
  },
});

export const externalGetEventsQuery = z.object({
  env: z.enum(ENVIRONMENTS_NAMES),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  lastUpdatedAt: z.coerce.date().optional(),
  includePermanent: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  enabled: z.preprocess(val => val === 'true' || val === '1', z.boolean()).optional(),
  type: z.enum(EVENT_TYPES).optional(),
  page: z.coerce.number().optional(),
  pageSize: z.coerce.number().optional(),
});

export const externalEventSchema = eventBaseSchema
  .pick({
    id: true,
    canonicalId: true,
    enabled: true,
    QAApproved: true,
    formatVersion: true,
    lastModifiedBy: true,
  })
  .and(eventComponentNoEnum);

export const externalEventErrorSchema = z.object({
  id: z.string(),
  canonicalId: z.string().optional(),
  name: z.string(),
  eventType: z.string(),
  issues: z.array(
    z.object({
      code: z.string(),
      path: z.array(z.union([z.string(), z.number()])),
      message: z.string(),
    }),
  ),
});

export const externalGetEventsSchema = validationSchema({
  response: {
    200: z.object({
      events: z.array(externalEventSchema),
      total: z.number(),
      totalPages: z.number(),
      // Events that failed response-schema validation and were excluded from
      // `events`. Empty when the whole feed is healthy. Lets consumers see
      // exactly which events are broken and why instead of getting a silent gap.
      errors: z.array(externalEventErrorSchema),
    }),
    500: z.object({ message: z.string() }),
  },
  querystring: externalGetEventsQuery,
});
